import { For, Show, type Component } from 'solid-js';
import { layerColour } from '~/view/layers';

/**
 * Legend for the layer colours a stacked strategy draws on the felt and the
 * wheel (SPEC-V2.md §4.5). A hue on a chip means nothing until something
 * names it, so this is the one place that naming happens — not decoration
 * under the felt, the key that makes the colours legible.
 */

export interface LayerLegendProps {
  /** Layer names in stack order — index is the colour key. */
  names: readonly string[];
  hoveredLayer?: number | null;
  onHoverLayer?: (index: number | null) => void;
  /**
   * `table` is the read-only phone surface, where everything is read at arm's
   * length in bad light. A caller should not have to reach in and restyle the
   * rows to get that — sizing that belongs to the component stays in it.
   */
  size?: 'compact' | 'table';
  class?: string;
}

const SIZES = {
  compact: { row: 'text-[10px] gap-1.5', swatch: 'h-2.5 w-2.5', gap: 'gap-x-3 gap-y-1.5' },
  table: { row: 'text-[15px] gap-2.5', swatch: 'h-4 w-4', gap: 'gap-x-5 gap-y-2.5' },
} as const;

const LayerLegend: Component<LayerLegendProps> = (props) => {
  // Matches the dimming the felt and wheel apply to non-hovered layers, so
  // the legend, the chips and the ring all read as one picture.
  const dimmed = (index: number): boolean =>
    props.hoveredLayer != null && props.hoveredLayer !== index;

  const size = () => SIZES[props.size ?? 'compact'];

  return (
    <Show when={props.names.length > 0}>
      <div class={`flex flex-wrap items-center ${size().gap} ${props.class ?? ''}`}>
        <For each={props.names}>
          {(name, index) => (
            <div
              class={`flex items-center ${size().row} text-ink-dim transition-opacity`}
              style={{ opacity: dimmed(index()) ? 0.4 : 1 }}
              onMouseEnter={() => props.onHoverLayer?.(index())}
              onMouseLeave={() => props.onHoverLayer?.(null)}
            >
              <span
                class={`${size().swatch} shrink-0 rounded-full`}
                style={{
                  background: layerColour(index()).hue,
                  'border-color': layerColour(index()).edge,
                  'border-style': 'solid',
                  'border-width': '1px',
                }}
              />
              <span class="truncate">{name}</span>
            </div>
          )}
        </For>
      </div>
    </Show>
  );
};

export default LayerLegend;
